//#region Model Imports

import { Enums } from '../common'
import { OpDataType } from '../core'

//#endregion

//#region Helpers

import { ConvertHelper, DataProvider, Dictionary } from '../../helpers'

//#endregion

//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

import { IOpObject, IOpData, IReferenceType } from './interfaces'
import { KnownColor, KnownColors } from '../common/known-colors'
import { DictionaryKeyType } from 'app/helpers/dictionary'

export class Utils {

    //#region Consts

    private static get JSON_COMPRESSION_FLAG(): string { return '__IC' }

    //#endregion

    public static isNullOrEmpty(value: string) {
        return !(value && value != '' && value != null)
    }

    public static isNullOrWhitespace(input: string) {
        if (typeof input === 'undefined' || input == null) return true
        return input.replace(/\s/g, '').length < 1
    }

    public static dateAdd(date, interval, units) {
        var ret = new Date(date); //don't change original date
        var checkRollover = function () { if (ret.getDate() != date.getDate()) ret.setDate(0); };
        switch (interval.toLowerCase()) {
            case 'year': ret.setFullYear(ret.getFullYear() + units); checkRollover(); break;
            case 'quarter': ret.setMonth(ret.getMonth() + 3 * units); checkRollover(); break;
            case 'month': ret.setMonth(ret.getMonth() + units); checkRollover(); break;
            case 'week': ret.setDate(ret.getDate() + 7 * units); break;
            case 'day': ret.setDate(ret.getDate() + units); break;
            case 'hour': ret.setTime(ret.getTime() + units * 3600000); break;
            case 'minute': ret.setTime(ret.getTime() + units * 60000); break;
            case 'second': ret.setTime(ret.getTime() + units * 1000); break;
            default: ret = undefined; break;
        }
        return ret;
    }

    static getHexColorFromSystemDrawingColor(color: { knownColor: number, state: number, value: number }): string {
        if (color) {
            if (color.state) {
                if (color.state === 1) {
                    const rgba: string = KnownColor.getColor(color.knownColor)
                    if (rgba) {
                        const rgbaSplit: string[] = rgba.split(',')
                        if (rgbaSplit.length === 4 && rgbaSplit[3] === '255') {
                            return '#' + rgbaSplit.slice(0, 3).map(c => parseInt(c).toString(16)).join('')
                        } else if (rgbaSplit.length === 4) {
                            return 'rgba(' + rgbaSplit.slice(0, 3).join(', ') + (+rgbaSplit[3] / 255).toFixed(2) + ')'
                        } else if (rgbaSplit.length === 4) {
                            return '#' + rgbaSplit.map(c => parseInt(c).toString(16)).join('')
                        }
                    }
                } else if (color.state === 2) {
                    const hex: string = color.value.toString(16)
                    if (hex.length === 8) {
                        var a = parseInt(hex.slice(0, 2), 16)
                        var r = parseInt(hex.slice(2, 4), 16)
                        var g = parseInt(hex.slice(4, 6), 16)
                        var b = parseInt(hex.slice(6, 8), 16)

                        return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + (a / 255).toFixed(2) + ')'
                    } else if (hex.length === 6) {
                        return '#' + hex
                    }
                } else {
                    console.warn('getColorFromKnownColor for state ' + color.state + ' is not implemented yet')
                }
            } else {

                const colorStr: string = color.toString()

                if (colorStr && colorStr.length && colorStr.length > 0) {

                    if (colorStr[0] === '#') {
                        return colorStr;
                    } else if (colorStr.indexOf('rgba') >= 0) {
                        const rgbaSplit: string[] = colorStr.replace("(", "").replace(")", "").replace("rgba", "").split(',')

                        if (rgbaSplit && rgbaSplit.length && rgbaSplit.length > 0) {
                            if (rgbaSplit.length === 4 && (rgbaSplit[3].indexOf(',') >= 0 || rgbaSplit[3].indexOf('.') >= 0)) {
                                rgbaSplit[3] = (255 * (+rgbaSplit[3])).toString()
                                return '#' + rgbaSplit.map(c => parseInt(c).toString(16)).join('')
                            } else {
                                return '#' + rgbaSplit.slice(0, 3).map(c => parseInt(c).toString(16)).join('')
                            }
                        }

                        console.warn('getColorFromKnownColor from string - failed to split')

                    } else {
                        const knownColor: KnownColors = ConvertHelper.parseEnum<KnownColors>(KnownColors, colorStr)

                        if (knownColor !== null) {
                            return Utils.getHexColorFromSystemDrawingColor({ knownColor: knownColor, state: 1, value: null })
                        } else {

                            const colorNumsSplit: number[] = colorStr.split(',').map(x => +(x.trim()))

                            if (colorNumsSplit && colorNumsSplit.length >= 3 && colorNumsSplit.filter(x => isFinite(x)).length >= 3) {
                                const joinedColor = colorNumsSplit.length > 3
                                    ? `rgba(${colorNumsSplit.join(", ")})`
                                    : `rgba(${colorNumsSplit.concat(255).join(", ")})`

                                return Utils.getHexColorFromSystemDrawingColor(<any>joinedColor)
                            }

                            console.warn('getColorFromKnownColor from string - unknown format')
                        }
                    }

                } else {
                    console.error('getColorFromKnownColor from string - value is null')
                }
            }
        }

        return '#000'
    }

    static isString(obj: any): boolean {
        return typeof obj === 'string'
    }


    //#region Compression 

    public static isJSONCompressed(obj: any): boolean {
        return obj != null && obj[Utils.JSON_COMPRESSION_FLAG] === 1
    }

    public static setJSONCompressedFlag(obj: any): void {
        if (obj != null) {
            obj[Utils.JSON_COMPRESSION_FLAG] = 1
        }
    }

    //#endregion

    //#region Copying

    public static copyIOpData<T extends IOpData>(target: T, source: T, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): void {

        if (includeNavigationProperties) {
            if (source.dataType != null) {
                target.dataType = source.dataType.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        target.idData = source.idData
        target.idDataType = source.idDataType
        target.indexNBR = source.indexNBR
        target.value = source.value
    }

    //#endregion

    //#region Dynamic values

    public static extractDynamicValueByIdDataType(obj: IOpObject, dataType: OpDataType): any {
        if (obj == null || dataType == null) {
            return null
        }

        const kvTuple: { Key: string, Value: any } = _.find(obj.dynamicValues,
            (kv: { Key: string, Value: any }) => kv.Key == dataType.idDataType.toString())

        if (kvTuple == null || kvTuple.Value == null) {
            return null
        }

        if (dataType.idDataTypeClass == Enums.DataTypeClass.Text) {
            return kvTuple.Value.trim()
        }

        return kvTuple.Value
    }

    //#endregion

    //#region Navigation Properties

    public static loadDataListNavigationProperties<TObject extends IOpObject>(dataProvider: DataProvider, list: TObject[]): JQueryPromise<TObject[]> {
        if (dataProvider != null && _.isArray(list)) {
            return dataProvider.System
                .getDictionaries([Enums.ReferenceType.IdDataType])
                .then((dict: Dictionary<Dictionary<IReferenceType>>) => {
                    let dataTypeDict: Dictionary<OpDataType> = null

                    if (dict.containsKey(Enums.ReferenceType.IdDataType)) {
                        dataTypeDict = <Dictionary<OpDataType>>dict.tryGetValue(Enums.ReferenceType.IdDataType)
                    }

                    if (dataTypeDict != null) {
                        list.forEach((item: IOpObject) => {
                            if (item.dataList != null) {
                                item.dataList
                                    .toArray()
                                    .forEach((data: IOpData) => {
                                        const dataType: OpDataType = dataTypeDict.tryGetValue(data.idDataType)

                                        if (dataType != null) {
                                            data.dataType = dataType
                                        }
                                    })
                            }
                        })
                    }

                    return list
                })
        }

        return $.Deferred().resolve(list).promise()
    }

    //#endregion

    //#region Serialization

    public static isJsonSupport(obj: any): boolean {
        return typeof obj.toJSON == 'function'
    }

    public static serializeDictionary<TValue>(dict: Dictionary<TValue>): Record<string, any> {
        const result: Record<string, any> = {}

        if (dict != null) {
            dict.forEach((key: DictionaryKeyType, value: TValue) => {
                if (Utils.isJsonSupport(value)) {
                    result[key] = (value as any).toJSON()
                }
                else {
                    result[key] = value
                }
            })
        }

        return result
    }

    //#endregion
}